import DeleteIconButton from '../components/DeleteIconButton';
import { Box, Divider, FormControl, Grid, InputLabel, Select, Switch, TextField, Tooltip, Typography } from '@mui/material';
import MaterialUiPhoneNumber from 'material-ui-phone-number';
import React, { useState } from 'react';

import { validateEmail } from '../utils';
import validURL from '../utils/validators/url';
import {
    SOCIAL_NETWORK_LABELS,
    SUPPORT_CONTACT_EMAIL,
    SUPPORT_CONTACT_PHONE,
    SUPPORT_CONTACT_SOCIAL,
    SUPPORT_CONTACT_SOURCE_LABELS,
    supportContactSubtypesOptions,
    supportContactTypesOptions,
} from './utils';

/** A social link's network, as the server derived it from the URL (read-only): its e-mail icon and name, and where
 *  the link came from when discovery found it. A new or edited link shows its network after saving. */
export const SocialNetworkBadge = React.memo(function SocialNetworkBadge({ data }) {
    const [iconFailed, setIconFailed] = useState(false);
    const label = SOCIAL_NETWORK_LABELS[data.network] || '';
    const source = SUPPORT_CONTACT_SOURCE_LABELS[data.source] || '';
    if (!label && !source) {
        return null;
    }
    return (
        <Box display="flex" alignItems="center" gap={1} sx={{ minHeight: 48 }}>
            {data.icon_url && !iconFailed ? (
                <img src={data.icon_url} alt="" width={24} height={24} onError={() => setIconFailed(true)} />
            ) : null}
            <Box>
                {label ? <Typography variant="body2">{label}</Typography> : null}
                {source ? (
                    <Typography variant="caption" color="text.secondary">
                        {source}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
});

const SupportContact = React.memo(function SupportContact(props) {
    const [contactEmailError, setContactEmailError] = useState(false);
    const [contactUrlError, setContactUrlError] = useState(false);
    const isSocial = +props.data.contact_type === SUPPORT_CONTACT_SOCIAL;

    const onChange = (ev) => {
        props.onChange(
            {
                ...props.data,
                [ev.target.name]: ev.target.name === 'contact_type' ? parseInt(ev.target.value) : ev.target.value,
                ...(ev.target.name === 'contact_type' && { contact: '' }) // Reset contact field when contact_type changes
            },
            props.idx
        );
    };

    const onSwitchChange = (ev) => {
        props.onChange(
            {
                ...props.data,
                enabled: !props.data.enabled,
            },
            props.idx
        );
    };

    const onPhoneChange = (number, numberProps) => {
        props.onChange(
            {
                ...props.data,
                contact: number,
            },
            props.idx
        );
    };

    const onDelete = (ev) => {
        props.onDelete && props.onDelete(props.idx);
    };

    const handleBlur = () => {
        if (props.data.contact_type === SUPPORT_CONTACT_EMAIL && !validateEmail(props.data.contact)) {
            setContactEmailError(true);
        } else {
            setContactEmailError(false);
        }
        setContactUrlError(isSocial && !!props.data.contact && !validURL(props.data.contact));
    };

    return (
        <React.Fragment>
            <Grid item xs={12} sm={3}>
                <TextField
                    disabled={props.disabled}
                    label="Label"
                    name="name"
                    value={props.data.name}
                    variant="filled"
                    fullWidth
                    size="small"
                    onChange={onChange}
                />
            </Grid>
            <Grid item xs={12} sm={2}>
                <FormControl variant="filled" size="small" fullWidth disabled={props.disabled}>
                    <InputLabel>Type</InputLabel>
                    <Select
                        native
                        value={props.data.contact_type}
                        name="contact_type"
                        onChange={onChange}
                    >
                        {supportContactTypesOptions}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
                {isSocial ? (
                    <SocialNetworkBadge data={props.data} />
                ) : (
                    <FormControl variant="filled" size="small" fullWidth disabled={props.disabled}>
                        <InputLabel>Department</InputLabel>
                        <Select native value={props.data.subtype} name="subtype" onChange={onChange}>
                            {supportContactSubtypesOptions}
                        </Select>
                    </FormControl>
                )}
            </Grid>
            <Grid item xs={12} sm={3}>
                {+props.data.contact_type === SUPPORT_CONTACT_PHONE ? (
                    <MaterialUiPhoneNumber
                        disabled={props.disabled}
                        autoComplete="off"
                        defaultCountry="us"
                        variant="filled"
                        size="small"
                        name="contact"
                        label="Contact"
                        fullWidth
                        inputProps={{
                            name: 'contact',
                        }}
                        onChange={onPhoneChange}
                        value={props.data?.contact || '+1'}
                    />
                ) : (
                        <TextField
                            disabled={props.disabled}
                            label={isSocial ? 'Profile link' : 'Contact'}
                            name="contact"
                            value={props.data.contact}
                            variant="filled"
                            fullWidth
                            size="small"
                            placeholder={isSocial ? 'https://www.instagram.com/yourcompany' : undefined}
                            onChange={onChange}
                            onBlur={handleBlur}
                            error={contactEmailError || contactUrlError}
                            helperText={
                                (contactEmailError && 'Please enter a valid email address') ||
                                (contactUrlError && 'Please enter a valid link')
                            }
                        />
                )}
            </Grid>
            <Grid item xs={6} sm={1} container alignItems="center">
                <Tooltip title={props.data.enabled ? 'Disable contact' : 'Enable contact'}>
                    <span>
                        <Switch
                            disabled={props.disabled}
                            checked={props.data.enabled}
                            name="enabled"
                            color="primary"
                            onChange={onSwitchChange}
                        />
                    </span>
                </Tooltip>
            </Grid>
            <Grid item xs={6} sm={1} container alignItems="center" justifyContent="flex-end">
                <DeleteIconButton title="Delete contact" onClick={onDelete} disabled={props.disabled} />
            </Grid>
            {props.isLast ? null : (
                <Grid item xs={12}>
                    <Divider />
                </Grid>
            )}
        </React.Fragment>
    );
});

export default SupportContact;
